import {useAuth} from '@apps-common/auth-react';
import CloseIcon from '@mui/icons-material/Close';
import {Box, Card, IconButton, Stack, Tooltip, Typography} from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {connect} from 'react-redux';

import {setCompareSubject} from '@explorer/actions/compare';
import ItemType from '@explorer/components/ui/item-type';
import {
  AreaCoverageIndicator,
  CloudCoverIndicator,
  IndicatorsContainer,
  PixelResolutionIndicator,
} from '@explorer/components/ui/metadata-indicators';
import ThumbnailImage from '@explorer/components/ui/thumbnail-image';
import ThumbnailStickyMap from '@explorer/components/ui/thumbnail-stickymap';
import {COMPARE_TYPES} from '@explorer/constants';
import formatMosaicDate from '@explorer/util/format-mosaic-date';
import {getResolution} from '@explorer/util/items';
import {getUrl as getThumbUrl} from '@explorer/util/thumbs';

const HEIGHT = 100;

const CompareMetaCard = props => {
  const auth = useAuth();
  const {t} = useTranslation();

  const hidden = !props.subject;

  const getCardContent = () => {
    const {type, item} = props.subject;
    const isMosaic = type === COMPARE_TYPES.MOSAIC;
    const isScene = type === COMPARE_TYPES.SCENE;
    const isResult = type === COMPARE_TYPES.RESULT;
    let thumbnail, header, subheader;

    switch (type) {
      case COMPARE_TYPES.MOSAIC:
        thumbnail = <ThumbnailImage src={item?._links.thumb} width={75} />;
        header = t(...formatMosaicDate(item));
        break;
      case COMPARE_TYPES.SCENE:
        thumbnail = (
          <ThumbnailImage
            src={getThumbUrl({item, token: auth.user?.accessToken, width: 75})}
          />
        );
        header = t('common:date.long', {
          date: item?.properties.acquired,
        });
        subheader = t('common:time.long', {
          date: item?.properties.acquired,
        });
        break;
      case COMPARE_TYPES.RESULT:
        {
          const dateString = t('common:date.long', {
            date: item?.properties.acquired,
          });
          thumbnail = (
            <ThumbnailStickyMap
              clip={props.clipGeometry}
              items={item?.properties.items}
              height={HEIGHT}
              width={75}
              ariaLabel={t('compare.thumbnailLabel', {date: dateString})}
            />
          );
          header = dateString;
        }
        break;
      default:
        break;
    }

    return (
      <React.Fragment>
        <Box
          sx={{
            width: '25%',
            maxWidth: 100,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {thumbnail}
        </Box>
        <Stack sx={{paddingX: 1, paddingY: 0.5, width: '75%'}}>
          <Typography
            variant="subtitle2"
            component="span"
            title={header}
            sx={{
              marginBottom: isMosaic ? 1 : 0,
            }}
          >
            {header}
          </Typography>
          {subheader && (
            <Typography variant="caption" sx={{fontSize: 12}}>
              {subheader}
            </Typography>
          )}
          {isMosaic && (
            <Typography
              variant="caption"
              gutterBottom
              sx={{
                display: 'block',
              }}
            >
              {item.seriesName}
            </Typography>
          )}
          {(isResult || isScene) && (
            <React.Fragment>
              <ItemType
                itemType={item.properties.item_type}
                typographyProps={{variant: 'caption'}}
              />

              <IndicatorsContainer mt={0.5}>
                <PixelResolutionIndicator
                  resolution={getResolution(item.properties.items[0] || item)}
                />
                {isResult && (
                  <AreaCoverageIndicator coverage={item.properties.coverage} />
                )}
                {isScene && (
                  <CloudCoverIndicator
                    cloudPercent={
                      item.properties.cloud_cover >= 0
                        ? Math.round(item.properties.cloud_cover * 100)
                        : null
                    }
                  />
                )}
              </IndicatorsContainer>
            </React.Fragment>
          )}
        </Stack>
      </React.Fragment>
    );
  };

  return (
    <Card
      sx={{
        minWidth: 330,
        maxWidth: 330,
        height: HEIGHT,
        display: 'flex',
        position: 'relative',
        visibility: hidden ? 'hidden' : 'visible',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0.75,
          right: 0.5,
        }}
      >
        <Tooltip title={t('compare.tooltipRemoveItem')}>
          <IconButton
            size="small"
            onClick={() => {
              props.setCompareSubject({index: props.index, subject: null});
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      {props.subject && getCardContent()}
    </Card>
  );
};

CompareMetaCard.propTypes = {
  clipGeometry: PropTypes.object,
  subject: PropTypes.object,
  index: PropTypes.number,
  setCompareSubject: PropTypes.func,
};

const mapStateToProps = state => ({
  clipGeometry: state.search.geometry,
});

const mapDispatchProps = {
  setCompareSubject,
};

export default connect(mapStateToProps, mapDispatchProps)(CompareMetaCard);
